/**
 * Portal / BMS packs define which top-level view tabs an instance gets.
 * Selected via GlobalConfig.vertical (and Instance.vertical in the control plane).
 *
 * - education → Student / Academics / Campus Life / Marketplace
 * - skyscraper → Community / Business / Management
 * - custom → starts from skyscraper tabs (operators can refine later)
 */

export type ViewModeId =
  | "home"
  | "student_life"
  | "academics"
  | "campus"
  | "marketplace"
  | "community"
  | "business"
  | "management";

export type PortalPackId =
  | "education"
  | "skyscraper"
  | "residential"
  | "retail"
  | "coworking"
  | "community"
  | "mixed"
  | "custom";

export type ViewModeAccent =
  | "emerald"
  | "violet"
  | "blue"
  | "amber"
  | "sky"
  | "rose";

export interface ViewModeDef {
  id: ViewModeId;
  label: string;
  title: string;
  accent: ViewModeAccent;
  /** lucide icon name key resolved in the toggle component */
  icon: "users" | "book" | "school" | "cart" | "briefcase" | "building" | "settings";
}

export interface PortalPackDef {
  id: PortalPackId;
  label: string;
  description: string;
  modes: ViewModeDef[];
  /** Optional sidebar section title overrides for this pack */
  sectionLabelOverrides?: Record<string, string>;
}

const EDUCATION_MODES: ViewModeDef[] = [
  {
    id: "student_life",
    label: "Student",
    title: "Student view",
    accent: "emerald",
    icon: "users",
  },
  {
    id: "academics",
    label: "Academics",
    title: "Academics view",
    accent: "violet",
    icon: "book",
  },
  {
    id: "campus",
    label: "Campus Life",
    title: "Campus Life view",
    accent: "blue",
    icon: "school",
  },
  {
    id: "marketplace",
    label: "Marketplace",
    title: "Marketplace view",
    accent: "amber",
    icon: "cart",
  },
];

const SKYSCRAPER_MODES: ViewModeDef[] = [
  {
    id: "community",
    label: "Community",
    title: "Community view",
    accent: "emerald",
    icon: "users",
  },
  {
    id: "business",
    label: "Business",
    title: "Business view",
    accent: "amber",
    icon: "briefcase",
  },
  {
    id: "management",
    label: "Management",
    title: "Management view",
    accent: "blue",
    icon: "building",
  },
];

export const PORTAL_PACKS: Record<string, PortalPackDef> = {
  education: {
    id: "education",
    label: "Educational institution",
    description:
      "Student, Academics, Campus Life, and Marketplace — campus-oriented modules.",
    modes: EDUCATION_MODES,
  },
  skyscraper: {
    id: "skyscraper",
    label: "Skyscraper / tower",
    description:
      "Community (peers + home marketplace), Business (professional listings), and Management.",
    modes: SKYSCRAPER_MODES,
    sectionLabelOverrides: {
      "Student Exchange": "Home marketplace",
      Marketplace: "Professional business",
      "Campus Life": "Building operations",
    },
  },
  residential: {
    id: "residential",
    label: "Residential / apartments",
    description: "Same navigation pattern as skyscraper towers.",
    modes: SKYSCRAPER_MODES,
    sectionLabelOverrides: {
      "Student Exchange": "Home marketplace",
      Marketplace: "Professional business",
      "Campus Life": "Building operations",
    },
  },
  retail: {
    id: "retail",
    label: "Retail / shopping mall",
    description: "Community + business + management for mall operators.",
    modes: SKYSCRAPER_MODES,
    sectionLabelOverrides: {
      "Student Exchange": "Peer marketplace",
      Marketplace: "Tenants & retail",
      "Campus Life": "Mall operations",
    },
  },
  coworking: {
    id: "coworking",
    label: "Coworking / innovation",
    description: "Member community, business listings, and site management.",
    modes: SKYSCRAPER_MODES,
  },
  community: {
    id: "community",
    label: "Community center",
    description: "Neighborhood community, local business, and facility management.",
    modes: SKYSCRAPER_MODES,
  },
  mixed: {
    id: "mixed",
    label: "Mixed / multi-purpose",
    description: "Defaults to education-style tabs until customized.",
    modes: EDUCATION_MODES,
  },
  custom: {
    id: "custom",
    label: "Custom",
    description:
      "Starts from skyscraper tabs; refine modules per customer requirements.",
    modes: SKYSCRAPER_MODES,
    sectionLabelOverrides: {
      "Student Exchange": "Peer marketplace",
      Marketplace: "Business",
      "Campus Life": "Management",
    },
  },
};

/** Map GlobalConfig / Instance.vertical → portal pack */
export function resolvePortalPack(vertical?: string | null): PortalPackDef {
  const key = (vertical || "mixed").toLowerCase();
  return PORTAL_PACKS[key] || PORTAL_PACKS.mixed;
}

export function isViewModeAllowed(
  pack: PortalPackDef,
  mode: ViewModeId,
): boolean {
  if (mode === "home") return true;
  return pack.modes.some((m) => m.id === mode);
}

export const ACCENT_ACTIVE_CLASS: Record<ViewModeAccent, string> = {
  emerald:
    "border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-transparent",
  violet:
    "border-violet-600 text-violet-600 dark:text-violet-400 bg-transparent",
  blue: "border-blue-600 text-blue-600 dark:text-blue-400 bg-transparent",
  amber: "border-amber-600 text-amber-600 dark:text-amber-400 bg-transparent",
  sky: "border-sky-600 text-sky-600 dark:text-sky-400 bg-transparent",
  rose: "border-rose-600 text-rose-600 dark:text-rose-400 bg-transparent",
};
